import React, { useState } from "react";
import { useTheme } from "@emotion/react";
import { Box, Chip, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { DescriptionOutlined, MoreVert, CheckCircleOutline } from "@mui/icons-material";
import moment from "moment";
import { getBucketUrl } from "utils";
import { useNavigate } from "react-router-dom";

const AnalysisCard = ({ result, onInvite, showBatchInfo = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showThumbnail, setShowThumbnail] = useState(true);

  const isPassed = result.status === "PASSED";
  const score = result.score || 0;

  const scoreColor = score >= 80 ? "#027A48" : score >= 70 ? "#0F6E56" : score >= 50 ? "#BA7517" : score >= 40 ? "#B54708" : "#B42318";
  const scoreBg = score >= 80 ? "#ECFDF3" : score >= 70 ? "#ECFDF3" : score >= 50 ? "#FFF6ED" : score >= 40 ? "#FFF6ED" : "#FEF3F2";
  const scoreBorder = score >= 70 ? "#ABEFC6" : score >= 40 ? "#FDDCAB" : "#FECDCA";

  const verdictLabel = score >= 80 ? "Strong match" : score >= 70 ? "Good match" : score >= 50 ? "Possible match" : score >= 40 ? "Weak match" : "Not a match";

  const cleanText = (str) => str ? str.replace(/\[id:[a-f0-9]+\]/gi, "").replace(/\s{2,}/g, " ").trim() : str;
  const getDisplayName = (name) => { const c = cleanText(name); return c || 'Untitled'; };

  const thumbnailSrc = result?.thumbnail_url
    ? `${result.thumbnail_url}`
    : null;

  return (
    <Box
      onClick={() => navigate(`/bulk-analysis/detail/${result.file_id}`)}
      sx={{
        border: `1px solid ${theme.palette.customColors.gray300}`,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
        },
        maxWidth: {xs: "100%", sm:"25rem"},
        marginInline: "auto",
        cursor: "pointer"
      }}
    >
      {/* Thumbnail area */}
      <Box
        sx={{
          // height: { xs: 140, sm: 160, md: 180 },
          backgroundColor: theme.palette.customColors.inputField,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          aspectRatio: "16 / 9",
        }}
      >
        {thumbnailSrc && showThumbnail ? (
          <img
            src={thumbnailSrc}
            alt={result.startup_name}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
            onError={() => setShowThumbnail(false)}
          />
        ) : (
          <DescriptionOutlined sx={{ fontSize: 64, color: theme.palette.customColors.b75 }} />
        )}

        {/* Score badge */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 40,
            backgroundColor: scoreBg,
            borderRadius: "6px",
            padding: "2px 8px",
            border: `1px solid ${scoreColor}33`,
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, color: scoreColor }}>
            {score}
          </Typography>
          <Typography variant="caption" sx={{ color: scoreColor, opacity: 0.7 }}>
            /100
          </Typography>
        </Box>

        {/* More options */}
        <IconButton
          size="small"
          sx={{ position: "absolute", top: 7, right: 4, backgroundColor: "white", "&:hover": { backgroundColor: "white" } }}
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          <MoreVert sx={{ fontSize: 18 }} />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={(e) => {
            e.stopPropagation();
            setAnchorEl(null)
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              padding: "12px",
              borderRadius: "8px",
              "& .MuiList-root": { padding: "0 !important" },
              bgcolor: theme.palette.customColors.g400,
              color: theme.palette.customColors.whiteBG,
              minWidth: "160px",
            },
          }}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
          anchorOrigin={{
            horizontal: "right",
            vertical: "bottom",
          }}
        >
          <MenuItem
            disableRipple
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              onInvite(result);
            }}
            disabled={result.invited}
            sx={{
              padding: "0.625rem 1rem",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              "&:hover": {
                backgroundColor: theme.palette.customColors.g500,
              },
            }}
          >
            {result.invited ? "Invited" : "Invite to Dealflow"}
          </MenuItem>
        </Menu>

        {/* File type badge */}
        {/* <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            backgroundColor: "#fff",
            borderRadius: "4px",
            padding: "1px 6px",
            border: `1px solid ${theme.palette.customColors.gray300}`,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 10 }}>PDF</Typography>
        </Box> */}
      </Box>

      {/* Card content */}
      <Box sx={{ padding: { xs: "0.75rem", sm: "1rem" }, flex: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }} noWrap>
            {getDisplayName(result.startup_name)}
          </Typography>
          <Chip
            label={verdictLabel}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: { xs: 9, sm: 11 },
              height: 22,
              flexShrink: 0,
              backgroundColor: scoreBg,
              color: scoreColor,
              border: `1px solid ${scoreBorder}`,
            }}
          />
        </Box>

        {showBatchInfo && (result.report_name || result.batch_created_at) && (
          <Typography variant="caption" sx={{ color: theme.palette.customColors.g200, mb: 0.5, display: "block", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
            {getDisplayName(result.report_name)}
            {result.batch_created_at && ` · ${moment(result.batch_created_at).format('DD-MMM-YYYY')}`}
          </Typography>
        )}

        {result.invited && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
            <CheckCircleOutline sx={{ fontSize: { xs: 12, sm: 14 }, color: "#027A48", flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: "#027A48", fontWeight: 600, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>Invited</Typography>
          </Box>
        )}

        {/* {result.analysis_pointers && result.analysis_pointers.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", color: theme.palette.customColors.g200, letterSpacing: 0.5, fontSize: { xs: "0.65rem", sm: "0.7rem" } }}>
              Analysis Pointers
            </Typography>
            {result.analysis_pointers.filter(pointer => pointer && !pointer.match(/^\[id:/)).map((pointer, idx) => (
              <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: theme.palette.customColors.b300, mt: "1px", fontSize: { xs: "0.65rem", sm: "0.75rem" }, flexShrink: 0 }}>
                  &rarr;
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.customColors.g200, lineHeight: 1.4, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                  {pointer}
                </Typography>
              </Box>
            ))}
          </Box>
        )} */}
      </Box>
    </Box>
  );
};

export default AnalysisCard;
